import UpdateButton from '../../../shared/UpdateButton/UpdateButton';
import {
  Input,
  InputGroup,
  Loader,
  RadioTile,
  RadioTileGroup,
  Slider,
  toaster,
  Toggle,
  Whisper,
} from 'rsuite';
import GearIcon from '@rsuite/icons/Gear';
import React, { FC, useEffect, useState } from 'react';
import DimmerApi from '../../../../services/Dimmer.api';
import moment from 'moment';
import StatusControl from '../../../shared/StatusControl/StatusControl';
import { getVoltageStyle } from '../../ExternalDevices/components/Dimmer/utils/Dimmer.utils';
import { useNavigate } from 'react-router-dom';
import { getTooltip } from '../../../../shared/Tooltip';
import { DimmerParams } from '../../../../models/Dimmer.model';
import NotificationFactory from '../../../../shared/Notifications';

export interface DimmerInfo {
  id: string;
  model: string;
  article: string;
  address: number;
  type: number;
  isConnected: boolean;
  isVisible: boolean;
}

export interface DimmerSectionProps {
  dimmer: DimmerInfo;
  deviceId: string;
}

const DimmerSection: FC<DimmerSectionProps> = ({ dimmer, deviceId }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [dimmerParams, setDimmerParams] = useState<DimmerParams | null>(null);
  const [schedulerMode, setSchedulerMode] = useState<string>('Manual');
  const [timeScheduler, setTimeScheduler] = useState<[Date, Date] | null>(null);
  const [duskDawnScheduler, setDuskDawnScheduler] = useState<[Date, Date] | null>(null);
  const navigate = useNavigate();

  const normalizeSchedulerMode = (mode: number | string | null | undefined): string => {
    if (mode === 'Timer' || mode === 'DuskDawn' || mode === 'Manual') {
      return mode;
    }

    if (mode === 1) {
      return 'Timer';
    }

    if (mode === 2) {
      return 'DuskDawn';
    }

    return 'Manual';
  };

  const getDimmerParams = async (): Promise<void> => {
    setLoading(true);
    try {
      const [result, descriptionsData] = await Promise.all([
        DimmerApi.getDimmerParams(dimmer?.id),
        DimmerApi.getDimmerDescription(dimmer?.id),
      ]);

      const channelDescriptions = descriptionsData?.details?.channels ?? [];
      const channelsWithDescriptions = (result.dimmer?.channels ?? []).map((channel, index) => {
        const channelDescription = channelDescriptions.find(
          (item: { channel?: number; description?: string }) => item.channel === index + 1,
        );

        return {
          ...channel,
          description: channelDescription?.description || '',
        };
      });

      setDimmerParams({
        ...result,
        dimmer: {
          ...result.dimmer,
          channels: channelsWithDescriptions,
        },
      });
      setSchedulerMode(normalizeSchedulerMode(result.dimmer?.schedulerMode));
    } catch (error) {
      toaster.push(
        NotificationFactory.error('Ошибка получения статуса диммера'),
        { placement: 'topEnd' },
      );
    } finally {
      setLoading(false);
    }
  };

  const getTimeScheduler = async (): Promise<void> => {
    try {
      const result = await DimmerApi.getTimeScheduler(dimmer?.id);

      const offTime = moment(result.offTime, 'HH:mm:ss').toDate();
      const onTime = moment(result.onTime, 'HH:mm:ss').toDate();

      setTimeScheduler([onTime, offTime]);
    } catch (error) {
      console.error(error);
    }
  };

  const getDuskDawnScheduler = async (): Promise<void> => {
    try {
      const result = await DimmerApi.getDuskDawnScheduler(dimmer?.id);

      const offTime = moment(result.offTime, 'HH:mm:ss').toDate();
      const onTime = moment(result.onTime, 'HH:mm:ss').toDate();

      setDuskDawnScheduler([offTime, onTime]);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!dimmer?.id) return;
    getDimmerParams().then();
    getTimeScheduler().then();
    getDuskDawnScheduler().then();
  }, [dimmer?.id]);

  const getTimeSchedulerText = (): string => {
    if (!timeScheduler || timeScheduler.length < 2) {
      return '';
    }

    return `${moment(timeScheduler[0]).format('HH:mm')} - ${moment(timeScheduler[1]).format('HH:mm')}`;
  };

  const getDuskDawnSchedulerText = (): string => {
    if (!duskDawnScheduler || duskDawnScheduler.length < 2) {
      return '';
    }

    return `${moment(duskDawnScheduler[1]).format('HH:mm')} - ${moment(duskDawnScheduler[0]).format('HH:mm')}`;
  };

  return (
    <div className="fieldset-wrapper">
      <div className="legend-title main-legend-title">Управление светом {dimmer?.model}</div>
      <section className="primary-clock">
        <div className="row">
          <div className="col-md-12 text-end">
            <UpdateButton loading={loading} onRefresh={getDimmerParams} />
          </div>
        </div>

        <div className="row align-items-center" style={{ padding: '10px', paddingTop: '0px' }}>
          <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <div className="mt-2 d-flex align-items-center justify-content-between">
              <Toggle
                size="sm"
                color="blue"
                checkedChildren="ON"
                unCheckedChildren="OFF"
                checked={false}
                loading={loading}
              >
                Эффекты вкл
              </Toggle>
              <Toggle
                size="sm"
                color="blue"
                checkedChildren="ON"
                unCheckedChildren="OFF"
                checked={!!dimmerParams?.effects?.effectsAlwaysEnabled}
                loading={loading}
              >
                Эффекты в люб. время
              </Toggle>
            </div>
          </div>
          <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 d-flex">
            <RadioTileGroup
              aria-label="Режим работы"
              className="main-page-dimmer-mode-group"
              disabled
              name="main-page-dimmer-mode"
              value={schedulerMode}
            >
              <RadioTile
                className="radio-tile"
                value="Manual"
                label="Ручной режим"
              >
                <span className="radio-tile-text">
                  Управление каналом вручную.
                </span>
              </RadioTile>

              <RadioTile
                className="radio-tile"
                value="Timer"
                label={`По времени: ${getTimeSchedulerText()}`}
              >
                <span className="radio-tile-text">
                  Работа по расписанию по заданному времени.
                </span>
              </RadioTile>

              <RadioTile
                className="radio-tile"
                value="DuskDawn"
                label={`Астрономическое реле: ${getDuskDawnSchedulerText()}`}
              >
                <span className="radio-tile-text">
                  Управление в зависимости от времени восхода и заката.
                </span>
              </RadioTile>
            </RadioTileGroup>
          </div>
        </div>

        <div className="row p-2">
          {dimmerParams?.dimmer.channels.length ? (
            dimmerParams.dimmer.channels.map((channel, index) => (
              <div className="col-md-6" key={index}>
                <section
                  className="section section-channels ps-3 mb-3"
                  style={{ borderRadius: '10px' }}
                >
                  <div className="row p-1 align-items-center">
                    <div
                      className="col-md-11 d-flex align-items-center"
                      style={{ marginLeft: '-5px' }}
                    >
                      <StatusControl status={channel?.isActive ? 'online' : 'offline'} />
                      <p className="me-3">{index + 1} Канал </p>
                      <div>
                        <Toggle
                          color="blue"
                          checkedChildren="ON"
                          unCheckedChildren="OFF"
                          disabled
                          size="sm"
                          checked={channel?.isEnabled}
                          loading={loading}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row mt-2 mb-2">
                    <div className="col-md-12 pe-4">
                      <Slider
                        value={channel.brightness}
                        progress
                        max={255}
                        tooltip={false}
                      />
                    </div>
                  </div>
                  <div className="row pe-2">
                    <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12">
                      <InputGroup>
                        <InputGroup.Addon>Напряжение</InputGroup.Addon>
                        <Input
                          style={getVoltageStyle(channel.voltage)}
                          value={channel.voltage ?? ''}
                          readOnly
                        />
                        <InputGroup.Addon>В</InputGroup.Addon>
                      </InputGroup>
                    </div>
                    <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12">
                      <InputGroup>
                        <InputGroup.Addon>Яркость</InputGroup.Addon>
                        <Input
                          value={Math.round((channel.brightness / 255) * 100)}
                          readOnly
                        />
                        <InputGroup.Addon>%</InputGroup.Addon>
                      </InputGroup>
                    </div>
                  </div>
                  <div className="row pe-2">
                    <div className="col-md-12 mt-2 mb-2">
                      <InputGroup>
                        <InputGroup.Addon>Описание</InputGroup.Addon>
                        <Input value={channel.description ?? ''} />
                      </InputGroup>
                    </div>
                  </div>
                </section>
              </div>
            ))
          ) : (
            <div className="text-center">
              <Loader size="lg" />
            </div>
          )}
        </div>

        <div className="settings">
          <a
            className="settings-link"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate(`/devices/${deviceId}?tab=devices&externalId=${dimmer?.id}`);
            }}
          >
            <Whisper
              placement="topStart"
              controlId="control-id-hover"
              trigger="hover"
              speaker={getTooltip('Перейти в настройки')}
            >
              <GearIcon className="gear-icon" />
            </Whisper>
          </a>
        </div>
      </section>
    </div>
  );
};

export default DimmerSection;
